import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Global, css } from '@emotion/react';

// Professional Design System Theme
import theme from './theme/theme';
import globalStyles from './theme/globalStyles';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import WhatsAppOnboarding from './components/auth/WhatsAppOnboarding';

// Layout Components
import Dashboard from './components/dashboard/Dashboard';
import WhatsAppAccounts from './components/whatsapp/WhatsAppAccounts';
import WhatsAppAnalytics from './components/whatsapp/WhatsAppAnalytics';
import Templates from './components/templates/Templates';
import TemplateBuilder from './components/templates/TemplateBuilder';
import Billing from './components/billing/Billing';
import Profile from './components/profile/Profile';
import NotFound from './components/layout/NotFound';
import PlanManagement from './components/admin/PlanManagement';
import UserManagement from './components/admin/UserManagement';
import SubscriptionManagement from './components/admin/SubscriptionManagement';
import DatabaseManagement from './components/admin/DatabaseManagement';
import MessageRates from './components/admin/MessageRates';
import InvoiceManagement from './components/admin/InvoiceManagement';
import PaymentsDashboard from './components/admin/PaymentsDashboard';
import AuditLog from './components/admin/AuditLog';
import ServerManagement from './components/admin/ServerManagement';

// Auth Context
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import PrivateRoute from './components/routing/PrivateRoute';
import AdminRoute from './components/routing/AdminRoute';


function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Global styles={css`${globalStyles}`} />
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />

              <Route path="/whatsapp-accounts" element={
                <PrivateRoute>
                  <WhatsAppAccounts />
                </PrivateRoute>
              } />

              <Route path="/whatsapp-analytics/:id" element={
                <PrivateRoute>
                  <WhatsAppAnalytics />
                </PrivateRoute>
              } />

              <Route path="/whatsapp-onboarding" element={
                <PrivateRoute>
                  <WhatsAppOnboarding />
                </PrivateRoute>
              } />

              {/* The old Messages page never sent through WhatsApp; keep old links working */}
              <Route path="/messages" element={<Navigate to="/dashboard" replace />} />

              <Route path="/templates" element={
                <PrivateRoute>
                  <Templates />
                </PrivateRoute>
              } />

              <Route path="/templates/new" element={
                <PrivateRoute>
                  <TemplateBuilder />
                </PrivateRoute>
              } />

              <Route path="/billing" element={
                <PrivateRoute>
                  <Billing />
                </PrivateRoute>
              } />

              <Route path="/profile" element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              } />

              <Route path="/admin/plans" element={
                <AdminRoute>
                  <PlanManagement />
                </AdminRoute>
              } />

              <Route path="/admin/users" element={
                <AdminRoute>
                  <UserManagement />
                </AdminRoute>
              } />

              <Route path="/admin/subscriptions" element={
                <AdminRoute>
                  <SubscriptionManagement />
                </AdminRoute>
              } />

              <Route path="/admin/payments" element={
                <AdminRoute>
                  <PaymentsDashboard />
                </AdminRoute>
              } />

              <Route path="/admin/invoices" element={
                <AdminRoute>
                  <InvoiceManagement />
                </AdminRoute>
              } />

              <Route path="/admin/rates" element={
                <AdminRoute>
                  <MessageRates />
                </AdminRoute>
              } />

              <Route path="/admin/servers" element={
                <AdminRoute>
                  <ServerManagement />
                </AdminRoute>
              } />

              <Route path="/admin/audit" element={
                <AdminRoute>
                  <AuditLog />
                </AdminRoute>
              } />

              <Route path="/admin/database" element={
                <AdminRoute>
                  <DatabaseManagement />
                </AdminRoute>
              } />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
